import { useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useWalletStore } from '@/stores/useWalletStore'
import { useLendStore } from '@/stores/useLendStore'
import { getCoinIcon } from '@/lib/common'
import { CommonFooter } from '@/components/layout'
import { toFixed, decdiv } from '@/lib/decimal'
import { handleFormatUserInfo } from '@/lib/lend-data'
import noDataImg from '@/assets/images/noData.svg'

export function LendMarketPage() {
  const { t } = useTranslation()
  const { session } = useWalletStore()
  const { marketList, fetchMarketList, fetchLendFarmApr, fetchWalletBalance, fetchUserLendData } = useLendStore()
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const accountName = session?.account?.name

  useEffect(() => {
    const init = async () => {
      await fetchMarketList()
      await fetchLendFarmApr()
      if (accountName) {
        await fetchWalletBalance(accountName)
        await fetchUserLendData(accountName)
      }
    }
    init()
    timerRef.current = setInterval(init, 10000)
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [accountName])

  const activeList = useMemo(() => marketList.filter((item: any) => item.is_active !== 0), [marketList])

  const stats = useMemo(() => {
    const info = handleFormatUserInfo(marketList)
    const totalMarketDeposit = marketList.reduce((sum: number, item: any) => {
      return sum + parseFloat(String(item.practical_balance?.split(' ')[0] || 0)) * (item.avg_price || 0)
    }, 0)
    const totalMarketBorrow = marketList.reduce((sum: number, item: any) => {
      return sum + parseFloat(String(item.total_borrows_variable?.split(' ')[0] || 0)) * (item.avg_price || 0)
    }, 0)
    return { ...info, totalMarketDeposit, totalMarketBorrow }
  }, [marketList])

  return (
    <div className="w-full flex justify-center px-4 md:px-[32px] md:mt-[32px]">
      <div className="w-full max-w-[600px]">
        {/* Stats board */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-[8px] mb-[16px]">
          {[
            { label: t('lend.totalDeposit'), value: stats.totalMarketDeposit > 0 ? '$' + toFixed(stats.totalMarketDeposit, 2) : '--' },
            { label: t('lend.totalBorrow'), value: stats.totalMarketBorrow > 0 ? '$' + toFixed(stats.totalMarketBorrow, 2) : '--' },
            { label: t('lend.myDeposit'), value: stats.totalDeposits > 0 ? '$' + toFixed(stats.totalDeposits, 2) : '--' },
            { label: t('lend.myBorrow'), value: stats.totalLoans > 0 ? '$' + toFixed(stats.totalLoans, 2) : '--' },
          ].map((item) => (
            <div key={item.label} className="bg-white rounded-[16px] shadow-[0_0_8px_1px_rgba(61,98,50,0.14)] p-[12px]">
              <p className="text-[12px] text-[#999] mb-[4px]">{item.label}</p>
              <p className="text-[14px] font-bold text-foreground">{item.value}</p>
            </div>
          ))}
        </div>

        {/* Market list */}
        <div className="bg-white rounded-[26px] shadow-[0_0_8px_1px_rgba(61,98,50,0.14)] px-[16px] py-[24px]">
          <h3 className="text-[16px] font-bold text-foreground mb-[16px]">{t('nav.market')}</h3>

          {activeList.length === 0 ? (
            <div className="flex flex-col items-center py-[30px]">
              <img src={noDataImg} className="w-[120px] mb-[12px]" alt="" />
              <span className="text-[14px] text-[#999]">{t('public.noData')}</span>
            </div>
          ) : (
            <div className="space-y-[10px]">
              {activeList.map((item: any) => {
                const depositRate = item.current_liquidity_rate ? parseFloat(decdiv(item.current_liquidity_rate, 1e14)) * 100 : 0
                const borrowRate = item.current_variable_borrow_rate ? parseFloat(decdiv(item.current_variable_borrow_rate, 1e14)) * 100 : 0
                const miningApr = (item.apr || 0) * 100

                return (
                  <div key={item.id} className="p-[12px] rounded-[16px] border border-[#e5e5e5]">
                    <div className="flex items-center gap-[8px] mb-[10px]">
                      <img src={getCoinIcon(item.contract, item.symbol)} className="w-[28px] h-[28px] rounded-full bg-gray-100" alt="" />
                      <span className="text-[14px] font-semibold text-foreground">{item.symbol}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-x-[16px] gap-y-[6px] text-[13px]">
                      <div className="flex justify-between">
                        <span className="text-[#666]">{t('lend.depositApy')}</span>
                        <span className="text-[#498846] font-semibold">
                          {toFixed(depositRate + miningApr, 2)}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#666]">{t('lend.borrowApy')}</span>
                        <span className="text-[#c0392b] font-semibold">
                          {toFixed(borrowRate, 2)}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#666]">{t('swap.poolReserves')}</span>
                        <span>{item.lastBalance > 0 ? toFixed(parseFloat(item.lastBalance), 4) : '--'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#666]">{t('lend.walletBalance')}</span>
                        <span>{item.walletBalance || '--'}</span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <CommonFooter />
      </div>
    </div>
  )
}
